/**
 * Typed, vendor-neutral configuration.
 *
 * `parseConfig` is PURE: it takes a raw env record (the caller supplies
 * `process.env` on a server or a VITE_-derived record in the browser) and
 * returns a validated, typed config. Nothing here reads the environment
 * directly, so it is trivially testable and platform-agnostic.
 *
 * Secrets live in the parsed config only where a server holds it; the browser
 * should pass only VITE_-exposed values. `featureFlags()` derives SAFE booleans
 * (no secret values) describing which online capabilities are configured — this
 * is what the UI uses to honestly show what needs internet.
 */

export type RawEnv = Record<string, string | undefined>;

export interface OwnerConfig {
  readonly handle: string;
  readonly name: string;
  readonly timezone: string;
}

export interface AiConfig {
  readonly enabled: boolean;
  readonly provider: string;
  readonly mode: string;
  readonly privacyMode: string;
  readonly zdrOnly: boolean;
  readonly freeOnly: boolean;
  readonly remoteEnabled: boolean;
  readonly openRouterKey: string | null;
  readonly openAiKey: string | null;
  readonly requestTimeoutMs: number;
  readonly maxModelsPerRequest: number;
  readonly defaultModel: string;
  readonly models: {
    readonly chat: readonly string[];
    readonly tool: readonly string[];
    readonly planning: readonly string[];
    readonly summarization: readonly string[];
    readonly coding: readonly string[];
    readonly vision: readonly string[];
    readonly audio: readonly string[];
  };
}

export interface SyncConfig {
  readonly enabled: boolean;
}

export interface StorageConfig {
  readonly enabled: boolean;
  readonly endpoint: string | null;
  readonly region: string;
  readonly bucket: string;
  readonly accessKeyId: string | null;
  readonly secretAccessKey: string | null;
}

export interface TelegramConfig {
  readonly enabled: boolean;
  readonly botToken: string | null;
  readonly allowedChatId: string | null;
}

export interface TwilioConfig {
  readonly whatsappEnabled: boolean;
  readonly telephonyEnabled: boolean;
  readonly accountSid: string | null;
  readonly authToken: string | null;
  readonly whatsappFrom: string | null;
  readonly voiceFrom: string | null;
}

export interface WebPushConfig {
  readonly enabled: boolean;
  readonly publicKey: string | null;
  readonly privateKey: string | null;
  readonly subject: string;
}

export interface SmtpConfig {
  readonly host: string | null;
  readonly port: number;
  readonly secure: boolean;
  readonly user: string | null;
  readonly from: string | null;
}

export interface LifeOsConfig {
  readonly owner: OwnerConfig;
  readonly appUrl: string;
  readonly publicBaseUrl: string;
  readonly databaseUrl: string | null;
  readonly syncApiUrl: string | null;
  readonly supabaseUrl: string | null;
  readonly supabaseAnonKey: string | null;
  readonly sync: SyncConfig;
  readonly storage: StorageConfig;
  readonly ai: AiConfig;
  readonly telegram: TelegramConfig;
  readonly twilio: TwilioConfig;
  readonly webPush: WebPushConfig;
  readonly smtp: SmtpConfig;
}

function str(env: RawEnv, key: string, def = ''): string {
  const v = env[key];
  return v === undefined ? def : v;
}

function optStr(env: RawEnv, key: string): string | null {
  const v = env[key];
  return v === undefined || v.trim() === '' ? null : v;
}

function bool(env: RawEnv, key: string, def = false): boolean {
  const v = env[key];
  if (v === undefined) return def;
  const trimmed = v.trim();
  if (trimmed === '') return def;
  return trimmed.toLowerCase() === 'true' || trimmed === '1' || trimmed.toLowerCase() === 'yes';
}

function num(env: RawEnv, key: string, def: number): number {
  const v = env[key];
  if (v === undefined || v.trim() === '') return def;
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

function list(env: RawEnv, key: string): readonly string[] {
  const v = env[key];
  if (!v) return [];
  return v
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Parse a raw env record into a typed, validated config. Pure. */
export function parseConfig(env: RawEnv = {}): LifeOsConfig {
  return {
    owner: {
      handle: str(env, 'LIFEOS_OWNER_HANDLE', 'owner'),
      name: str(env, 'LIFEOS_OWNER_NAME', 'Owner'),
      timezone: str(env, 'LIFEOS_TIMEZONE', 'UTC'),
    },
    appUrl: str(env, 'VITE_APP_URL', 'http://localhost:5173'),
    publicBaseUrl: str(env, 'LIFEOS_PUBLIC_BASE_URL', 'http://localhost:5173'),
    databaseUrl: optStr(env, 'DATABASE_URL'),
    syncApiUrl: optStr(env, 'LIFEOS_SYNC_API_URL') ?? optStr(env, 'VITE_LIFEOS_SYNC_API_URL'),
    supabaseUrl: optStr(env, 'VITE_SUPABASE_URL'),
    supabaseAnonKey: optStr(env, 'VITE_SUPABASE_ANON_KEY'),
    sync: {
      enabled:
        optStr(env, 'DATABASE_URL') !== null ||
        optStr(env, 'LIFEOS_SYNC_API_URL') !== null ||
        optStr(env, 'VITE_LIFEOS_SYNC_API_URL') !== null ||
        optStr(env, 'VITE_SUPABASE_URL') !== null,
    },
    storage: {
      enabled:
        optStr(env, 'S3_ENDPOINT') !== null && optStr(env, 'S3_ACCESS_KEY_ID') !== null,
      endpoint: optStr(env, 'S3_ENDPOINT'),
      region: str(env, 'S3_REGION', 'auto'),
      bucket: str(env, 'S3_BUCKET', 'life-os'),
      accessKeyId: optStr(env, 'S3_ACCESS_KEY_ID'),
      secretAccessKey: optStr(env, 'S3_SECRET_ACCESS_KEY'),
    },
    ai: {
      enabled:
        optStr(env, 'OPENROUTER_API_KEY') !== null ||
        optStr(env, 'OPENAI_API_KEY') !== null,
      provider: str(env, 'LIFEOS_AI_PROVIDER', 'openrouter'),
      mode: str(env, 'LIFEOS_AI_MODE', 'openrouter_zdr_free'),
      privacyMode: str(env, 'LIFEOS_AI_PRIVACY_MODE', 'strict'),
      zdrOnly: bool(env, 'LIFEOS_AI_ZDR_ONLY', true),
      freeOnly: bool(env, 'LIFEOS_AI_FREE_ONLY', true),
      remoteEnabled: bool(env, 'LIFEOS_AI_REMOTE_ENABLED', false),
      openRouterKey: optStr(env, 'OPENROUTER_API_KEY'),
      openAiKey: optStr(env, 'OPENAI_API_KEY'),
      requestTimeoutMs: num(env, 'LIFEOS_AI_REQUEST_TIMEOUT_MS', 20_000),
      maxModelsPerRequest: num(env, 'LIFEOS_AI_MAX_MODELS_PER_REQUEST', 5),
      defaultModel: str(env, 'LIFEOS_AI_MODEL', 'openrouter/free'),
      models: {
        chat: list(env, 'LIFEOS_AI_CHAT_MODELS'),
        tool: list(env, 'LIFEOS_AI_TOOL_MODELS'),
        planning: list(env, 'LIFEOS_AI_PLANNING_MODELS'),
        summarization: list(env, 'LIFEOS_AI_SUMMARIZATION_MODELS'),
        coding: list(env, 'LIFEOS_AI_CODING_MODELS'),
        vision: list(env, 'LIFEOS_AI_VISION_MODELS'),
        audio: list(env, 'LIFEOS_AI_AUDIO_MODELS'),
      },
    },
    telegram: {
      enabled:
        bool(env, 'LIFEOS_AI_TELEGRAM_ENABLED', false) &&
        optStr(env, 'LIFEOS_TELEGRAM_BOT_TOKEN') !== null,
      botToken: optStr(env, 'LIFEOS_TELEGRAM_BOT_TOKEN'),
      allowedChatId: optStr(env, 'LIFEOS_TELEGRAM_ALLOWED_CHAT_ID'),
    },
    twilio: {
      whatsappEnabled: bool(env, 'LIFEOS_AI_WHATSAPP_ENABLED', false),
      telephonyEnabled: bool(env, 'LIFEOS_AI_TELEPHONY_ENABLED', false),
      accountSid: optStr(env, 'LIFEOS_TWILIO_ACCOUNT_SID'),
      authToken: optStr(env, 'LIFEOS_TWILIO_AUTH_TOKEN'),
      whatsappFrom: optStr(env, 'LIFEOS_TWILIO_WHATSAPP_FROM'),
      voiceFrom: optStr(env, 'LIFEOS_TWILIO_VOICE_FROM'),
    },
    webPush: {
      enabled: bool(env, 'LIFEOS_AI_WEB_PUSH_ENABLED', false),
      publicKey: optStr(env, 'VITE_WEB_PUSH_PUBLIC_KEY'),
      privateKey: optStr(env, 'LIFEOS_WEB_PUSH_PRIVATE_KEY'),
      subject: str(env, 'LIFEOS_WEB_PUSH_SUBJECT', ''),
    },
    smtp: {
      host: optStr(env, 'LIFEOS_SMTP_HOST'),
      port: num(env, 'LIFEOS_SMTP_PORT', 587),
      secure: bool(env, 'LIFEOS_SMTP_SECURE', false),
      user: optStr(env, 'LIFEOS_SMTP_USER'),
      from: optStr(env, 'LIFEOS_SMTP_FROM'),
    },
  };
}

/** Safe, secret-free booleans describing which online capabilities are configured. */
export interface FeatureFlags {
  readonly cloudSync: boolean;
  readonly cloudBackup: boolean;
  readonly ai: boolean;
  readonly email: boolean;
  readonly telegram: boolean;
  readonly whatsapp: boolean;
  readonly voiceCalls: boolean;
  readonly webPush: boolean;
}

/** Derive feature availability from config. Pure; returns no secret values. */
export function featureFlags(config: LifeOsConfig): FeatureFlags {
  return {
    cloudSync: config.databaseUrl !== null || config.syncApiUrl !== null || config.supabaseUrl !== null,
    cloudBackup:
      config.storage.endpoint !== null &&
      config.storage.accessKeyId !== null &&
      config.storage.secretAccessKey !== null,
    ai: config.ai.openRouterKey !== null || config.ai.openAiKey !== null,
    email: config.smtp.host !== null && config.smtp.user !== null,
    telegram: config.telegram.enabled && config.telegram.botToken !== null,
    whatsapp:
      config.twilio.whatsappEnabled &&
      config.twilio.accountSid !== null &&
      config.twilio.authToken !== null &&
      config.twilio.whatsappFrom !== null,
    voiceCalls:
      config.twilio.telephonyEnabled &&
      config.twilio.accountSid !== null &&
      config.twilio.authToken !== null &&
      config.twilio.voiceFrom !== null,
    webPush: config.webPush.enabled && config.webPush.publicKey !== null && config.webPush.privateKey !== null,
  };
}

/**
 * Surface "enabled but incomplete" misconfigurations so the app can fail loudly
 * instead of silently doing nothing. Pure; returns human-readable warnings.
 */
export function configWarnings(config: LifeOsConfig): readonly string[] {
  const warns: string[] = [];
  if (config.telegram.enabled && config.telegram.botToken === null) {
    warns.push('Telegram is enabled but LIFEOS_TELEGRAM_BOT_TOKEN is missing.');
  }
  if (config.twilio.whatsappEnabled && (config.twilio.accountSid === null || config.twilio.authToken === null)) {
    warns.push('WhatsApp is enabled but Twilio credentials are incomplete.');
  }
  if (config.twilio.telephonyEnabled && (config.twilio.accountSid === null || config.twilio.voiceFrom === null)) {
    warns.push('Voice calls are enabled but Twilio voice config is incomplete.');
  }
  if (config.webPush.enabled && (config.webPush.publicKey === null || config.webPush.privateKey === null)) {
    warns.push('Web Push is enabled but VAPID keys are missing.');
  }
  if (config.ai.remoteEnabled && config.ai.openRouterKey === null && config.ai.openAiKey === null) {
    warns.push('Remote AI is enabled but no AI provider key is set.');
  }
  if (
    (config.storage.endpoint !== null || config.storage.accessKeyId !== null) &&
    (config.storage.endpoint === null || config.storage.accessKeyId === null || config.storage.secretAccessKey === null)
  ) {
    warns.push('Object storage is partially configured; endpoint + access key + secret are all required.');
  }
  return warns;
}

/** List of enabled capability names. Pure; returns no secret values. */
export function enabledCapabilities(config: LifeOsConfig): readonly string[] {
  const caps: string[] = [];
  const flags = featureFlags(config);
  if (flags.ai) caps.push('ai');
  if (flags.cloudSync) caps.push('sync');
  if (flags.email) caps.push('email');
  if (flags.telegram) caps.push('telegram');
  if (flags.whatsapp) caps.push('whatsapp');
  if (flags.voiceCalls) caps.push('voice_calls');
  if (flags.webPush) caps.push('web_push');
  if (config.storage.enabled) caps.push('storage');
  return caps;
}

/** Redacted config summary suitable for logging/admin. Pure; never leaks secrets. */
export function redactedConfigSummary(config: LifeOsConfig): Record<string, boolean | string> {
  return {
    appUrl: config.appUrl,
    publicBaseUrl: config.publicBaseUrl,
    timezone: config.owner.timezone,
    ...Object.fromEntries(enabledCapabilities(config).map((c) => [c, true])),
    ai_freeOnly: config.ai.freeOnly,
    ai_zdrOnly: config.ai.zdrOnly,
    ai_mode: config.ai.mode,
    ai_privacyMode: config.ai.privacyMode,
    sync_enabled: config.sync.enabled,
    storage_region: config.storage.region,
    storage_bucket: config.storage.bucket,
    smtp_port: String(config.smtp.port),
    smtp_secure: config.smtp.secure,
    webPush_enabled: config.webPush.enabled,
    telegram_enabled: config.telegram.enabled,
    whatsapp_enabled: config.twilio.whatsappEnabled,
    voice_enabled: config.twilio.telephonyEnabled,
  };
}
